/**
 * Safe HTML helpers.
 *
 * Both helpers funnel content through DOMPurify (or a `<script>`-safe JSON
 * escaper) so callers never have to touch `dangerouslySetInnerHTML` directly.
 * The dangerously-set call lives in a *single* place per renderer below.
 */
import DOMPurify from "dompurify";

const sanitizeHtml = (dirty) => {
  if (typeof dirty !== "string" || !dirty) return "";
  return DOMPurify.sanitize(dirty, { USE_PROFILES: { html: true } });
};

const escapeForScriptTag = (obj) =>
  JSON.stringify(obj)
    .replace(/</g, "\\u003c")
    .replace(/-->/g, "--\\u003e");

/**
 * Renders sanitized HTML inline. Always preferred over plain
 * `dangerouslySetInnerHTML` because the input is forced through DOMPurify here.
 *
 * Example:
 *   <SafeHtml html={article.body} className="prose" />
 */
export const SafeHtml = ({ html, as: Tag = "div", ...rest }) => (
  // eslint-disable-next-line react/no-danger -- input sanitized by DOMPurify above.
  <Tag {...rest} dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
);

/**
 * Renders a Schema.org JSON-LD `<script>` block safely. Escapes `<` and
 * `-->` so user-supplied text inside the JSON can never break out of
 * the surrounding `<script>` tag.
 */
export const SafeJsonLd = ({ data }) => (
  // eslint-disable-next-line react/no-danger -- JSON is escaped by escapeForScriptTag.
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: escapeForScriptTag(data) }}
  />
);

// Legacy named exports (still used by some callers; same behavior)
export const safeHtml = sanitizeHtml;
export const safeJsonLd = escapeForScriptTag;

export default SafeHtml;
